import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  EditOutlined
} from "@ant-design/icons"
import { Button, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { homeColor, homeText } from "../home-theme.ts"

/**
 * The sticky section nav overlaps whatever a jump lands on, so the heading
 * reserves the nav's height above itself (CSS, so a plain `#hash` load works
 * too).
 */
const SCROLL_MARGIN = 64

interface HomeSectionHeadingProps {
  anchorId: string
  label: string
  icon?: React.ReactNode
  /** Edit layout: the group's handle, rename, and one-step moves. */
  editing?: {
    handle: React.ReactNode
    canMoveUp: boolean
    canMoveDown: boolean
    onMove: (direction: "up" | "down") => void
    onRename: () => void
  } | null
}

/**
 * A section's heading — the first line of the section BLOCK, which also
 * holds the section's widgets, so dragging the handle here carries the whole
 * group. Deliberately lighter than the page title and without a caption: each
 * widget below says what it shows in its own header.
 */
export const HomeSectionHeading = ({
  anchorId,
  label,
  icon,
  editing
}: HomeSectionHeadingProps) => {
  const { t } = useTranslation()

  return (
    <div
      id={anchorId}
      tabIndex={-1}
      className="home-section-heading"
      style={{ scrollMarginTop: SCROLL_MARGIN }}
    >
      {editing?.handle}
      {icon ? (
        <span aria-hidden className="home-section-heading__icon">
          {icon}
        </span>
      ) : null}
      <h2
        id={`${anchorId}-title`}
        className="m-0"
        style={{ ...homeText.itemTitle, color: homeColor.ink }}
      >
        {label}
      </h2>
      {editing ? (
        <span className="home-section-heading__action">
          <Tooltip title={t("home.layout.renameSection", "Rename section")}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              aria-label={t(
                "home.layout.renameSectionNamed",
                "Rename {{title}}",
                {
                  title: label
                }
              )}
              onClick={editing.onRename}
            />
          </Tooltip>
          <Tooltip title={t("home.layout.sectionUp", "Move section up")}>
            <Button
              type="text"
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={!editing.canMoveUp}
              aria-label={t("home.layout.sectionUpNamed", "Move {{title}} up", {
                title: label
              })}
              onClick={() => editing.onMove("up")}
            />
          </Tooltip>
          <Tooltip title={t("home.layout.sectionDown", "Move section down")}>
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={!editing.canMoveDown}
              aria-label={t(
                "home.layout.sectionDownNamed",
                "Move {{title}} down",
                { title: label }
              )}
              onClick={() => editing.onMove("down")}
            />
          </Tooltip>
        </span>
      ) : null}
    </div>
  )
}
